import videojs from 'video.js';
const Component = videojs.getComponent('Component');

/**
 * @typedef {Object} Gap
 * @property {number} start - Начальное значение интервала
 * @property {number} finish - Конечное значение интервала
 */

/**
 * @typedef {Object} Quote
 * @property {number} id - Идентификатор цитаты
 * @property {number} client_id - Идентификатор клиента
 * @property {number} event_id - Идентификатор события
 * @property {number} quote_type_id - Идентификатор типа цитаты
 * @property {string} created_at - Дата создания в формате ISO 8601
 * @property {string} description - Описание цитаты
 * @property {number} start - Начальное значение интервала
 * @property {number} finish - Конечное значение интервала
 */

/**
 * @typedef {Object} Events
 * @property {Gap[]} gaps - Массив с информацией об интервалах
 * @property {Quote[]} quotes - Массив с информацией о цитатах
 */

class EventsOnTimeline extends Component {
	/**
	 * @param {import("video.js").VideoJsPlayer} player
	 * @param {import("./videojs-events-on-timeline").Options} options
	 */
	constructor(player, options) {
		super(player, options);
		/** @type Quote[] */
		this.quoteList = [];
		/** @type Gap[] */
		this.gaps = [];
		this.currentBroadcastStartAt = this.options_.startBroadcast;
		this.currentBroadcastFinishAt = this.options_.endBroadcast;
		this.init();
	}

	init() {
		this.eventsUrl = this.options_.eventsUrl;
		this.requestQuotes();
		this.setInterval(() => this.requestQuotes(), this.options_.pollingInterval);

		if (this.checkHidden()) {
			this.hide();
		}

		this.player().on(['play', 'durationchange', 'fullscreenchange', 'playerresize'], () => {
			if (this.checkHidden()) {
				this.hide();
			} else {
				this.show();
				this.updateQuotesPosition();
			}
		});

		this.player().on('MulticamCamera/select', (data) => {
			setTimeout(() => { // Таймаут связан с особенностями переключения камер.
				const selectedCamera = this.player().multicam().getCameras().find((camera) => camera.checkSelected());
				this.currentBroadcastStartAt = selectedCamera.startAt;
				this.currentBroadcastFinishAt = selectedCamera.finishAt;
				const eventsUrl = new URL(this.eventsUrl);
				// Пропуски для камеры могут быть своими, добавляем соответствующий параметр запроса
				eventsUrl.searchParams.set('gaps_for', selectedCamera.getBroadcastId());
				this.eventsUrl = eventsUrl.toString();
				this.requestQuotes();
			});
		});
	}

	async requestQuotes() {
		const events = await this.getQuotes(this.eventsUrl);
		this.setGaps(events);
		this.setEvents(events);
	}

	checkHidden() {
		const duration = this.player().duration();
		return Number.isNaN(duration) || duration === 0;
	}

	/**
	 * @param {Events} events
	 */
	setEvents(events) {
		this.quoteList = this.prepareEvents(events);
		this.renderEvents(this.quoteList);
	}

	/**
	 * @param {Events} events
	 */
	setGaps(events) {
		this.gaps = events.gaps.reduce((acc, gap) => {
			if (this.currentBroadcastStartAt > gap.finish) {
				return acc;
			}
			if (gap.start < this.currentBroadcastStartAt && this.currentBroadcastStartAt < gap.finish) {
				return {
					start: this.currentBroadcastStartAt,
					finish: gap.finish
				};
			}
			acc.push(gap);
			return acc;
		}, []);
	}

	async getQuotes(url) {
		const response = await fetch(url);
		return await response.json();
	}

	/**
	 * @param {Events} events
	 * @returns {Quote[]}
	 */
	prepareEvents(events) {
		const quotesNotInGap = events.quotes.filter((quote) => {
			return !events.gaps.some((gap) => {
				const isStartInQuote = gap.start >= quote.start && gap.start <= quote.finish;
				const isFinishInQuote = gap.finish >= quote.start && gap.finish <= quote.finish;
				return isStartInQuote || isFinishInQuote;
			});
		});
		const sortedQuotes = [...quotesNotInGap].sort((a, b) => a.start - b.start);
		const visibleQuotes = sortedQuotes.filter((quote) => {
			return quote.start >= this.currentBroadcastStartAt &&
				quote.start < this.currentBroadcastFinishAt;
		});
		const quotesWithFixedStart = visibleQuotes.map((quote) => {
			const quoteCopy = {...quote};
			events.gaps.forEach((gap) => {
				if (gap.start <= quoteCopy.start && quoteCopy.start <= gap.finish) {
					quoteCopy.start = gap.finish;
				}
			});
			return quoteCopy;
		});
		return quotesWithFixedStart;
	}

	/**
	 * @param {Quote} quotes
	 */
	getQuoteTimeOnTimeline(quote) {
		const gapsSum = this.gaps
			.filter((gap) => gap.start < quote.start)
			.reduce((acc, gap) => acc + gap.finish - gap.start, 0);
		return quote.start - this.currentBroadcastStartAt - gapsSum;
	}

	/**
	 * @param {Quote[]} quotes
	 */
	renderEvents(quotes) {
		this.cleanQuotes();

		if (quotes.length) {

			function isLocalStorageAvailable(){
				let test = 'test';
				try {
					localStorage.setItem(test, test);
					localStorage.removeItem(test);
					return true;
				} catch(e) {
					return false;
				}
			}

			const isStorageAvailable = isLocalStorageAvailable();
			let value = isStorageAvailable ? localStorage.getItem('wc_player_isCloseEvents') : false;
			let isCloseEvents = value == "true" ? true : false;

			const toggleEventsVisibility = this.addChild('ClickableComponent', {
				clickHandler: () => {
					isCloseEvents = toggleEventsVisibility.el().classList.toggle('vjs-events-on-timeline__close');
					isStorageAvailable && localStorage.setItem('wc_player_isCloseEvents', isCloseEvents);
				},
				className: 'vjs-events-on-timeline__toggle',
				controlText: 'События'
			});
			toggleEventsVisibility.setAttribute("data-time", "0");

			isCloseEvents && toggleEventsVisibility.addClass('vjs-events-on-timeline__close');
			this.addChild(toggleEventsVisibility);
		}

		quotes.forEach((quote) => {
			const { quote_type_id } = quote;
			/** @type string */
			const iconUrl = this.options_.getIconUrl(quote_type_id);
			if (!iconUrl) return;
			const time = this.getQuoteTimeOnTimeline(quote);
			const component = this.addChild('ClickableComponent', {
				clickHandler: () => {
					if (!this.options_.isAvailable) {
						const restrictionModalDialog = this.player().getChild('restrictionModalDialog');
						if (!restrictionModalDialog) return;
						restrictionModalDialog.toggleDisplay();
						restrictionModalDialog.onGetAccess(() => {
							this.options_.requestAccess();
						});
						return;
					}
					this.player().currentTime(time);
				},
				className: 'vjs-events-on-timeline__item'
			});
			if (!this.options_.isAvailable) {
				component.addClass('vjs-events-on-timeline__item_disabled');
			}
			component.setAttribute('data-time', time); // На quote.id завязаться не получится, т.к. он не уникальный.
			const icon = videojs.dom.createEl('img', {
				className: 'vjs-events-on-timeline__icon'
			}, {
				src: iconUrl
			});
			component.el().appendChild(icon);
			this.addChild(component);
		});
		this.updateQuotesPosition();
		this.player().trigger('eventsOnTimeline/eventsRendered');
	}

	updateQuotesPosition() {
		const itemWidth = 24 / this.currentWidth() * 100; // %
		const children = this.children().filter((child) => !child.isDisposed());
		const adjustIndentsToFitWidth = (indents) => {
			if (indents[indents.length - 1] + itemWidth / 2 > 100) {
				return indents
					.reverse()
					.reduce(function shiftToLeft(acc, indent, index) {
						if (index === 0) {
							return acc.concat(100 - itemWidth / 2);
						}
						const isOverlay = indent + itemWidth > acc[index - 1];
						if (isOverlay) {
							return acc.concat(acc[index - 1] - itemWidth);
						}
						return acc.concat(indent);
					}, [])
					.reverse();
			}
			return indents;
		};
		adjustIndentsToFitWidth(
			children
				.map(function getLeftIndent(child) {
					const quoteTime = Number(child.getAttribute('data-time'));
					const liveTracker = this.player().liveTracker;
					//если это лайв трансляция, то для вычисления videoDuration
					//нужно использовать не this.player().duration(), а liveTracker.seekableEnd(),
					//так как в случае лайва длительность трансляции Infinity,
					//но при этом для лайва есть liveTracker.seekableEnd() - доступная в данный момент длительность трансляции
					const videoDuration = liveTracker && liveTracker.isLive() ? liveTracker.seekableEnd() : this.player().duration();
					return quoteTime / videoDuration * 100;
				}.bind(this))
				.reduce(function fixOverlay(acc, indent, index) {
					const isOverlay = (index > 0) && (acc[index - 1] + itemWidth > indent);
					if (index === 0 ) {
						return acc.concat(indent + itemWidth / 2);
					}
					if (!isOverlay) {
						return acc.concat(indent);
					}
					return acc.concat(acc[index - 1] + itemWidth);
				}, [])
		)
		.forEach(function applyIndent(indent, index) {
			children[index].el().style.left = `${indent}%`;
		});
	}

	createEl() {
		return super.createEl('div', {
			className: 'vjs-events-on-timeline__container'
		});
	}

	cleanQuotes() {
		this.children().forEach((child) => child.dispose());
	}
}

Component.registerComponent('EventsOnTimeline', EventsOnTimeline);
export default EventsOnTimeline;
